// The two halves of a bulk status change the owner asked for (2026-09-18):
// say how many elements a tree node will change BEFORE changing them, and
// offer to undo it after.
import React from "react";
import { Modal, message } from "antd";
import { previewElementStatus, setElementStatus, undoElementStatus } from "api/tracking/trackingElementsApi";
import { STATUS_LABEL, STATUS_ORDER } from "./trackingTheme";
import { stageContainer } from "./stagePopup";

const n = (v) => Number(v || 0).toLocaleString();

// "Set 1,240 elements to Installed?" with where they stand now, and the
// move-back warning folded into the same question.
export const confirmBulk = (pv, status, what = "") => new Promise((resolve) => {
  const now = STATUS_ORDER.filter((s) => pv.byCurrent?.[s]).map((s) => `${n(pv.byCurrent[s])} ${STATUS_LABEL[s].toLowerCase()}`).join(", ");
  Modal.confirm({
    // A PRECISE ACTION DESERVES A PRECISE SENTENCE (leader, 2026-09-20).
    // Since a selection can now be replaced by a single click, "Set 1,240
    // elements to Installed?" no longer says enough - the question has to
    // name WHICH 1,240: "Mark 1,240 elements on Level 2 as Installed?".
    // `what` is the selection in words; without one this reads exactly as
    // it always did.
    title: what
      ? `Mark ${n(pv.count)} element${pv.count === 1 ? "" : "s"} ${what} as ${STATUS_LABEL[status]}?`
      : `Set ${n(pv.count)} element${pv.count === 1 ? "" : "s"} to ${STATUS_LABEL[status]}?`,
    content: (
      <div>
        {now ? <div>Now: {now}.</div> : null}
        <div>{n(pv.wouldChange)} will change.</div>
        {pv.wouldDowngrade ? <div style={{ color: "#cf1322", marginTop: 6 }}>{n(pv.wouldDowngrade)} of them are further along and will move BACK to {STATUS_LABEL[status]}.</div> : null}
        {/* The caveat carries its NUMBER, or it is one people skip; and the
            undo has to name a door that is still there after the toast
            fades (leader, 2026-09-20). */}
        <div style={{ marginTop: 6, color: "#8c8c8c" }}>
          {pv.excluded
            ? `${n(pv.excluded)} more element${pv.excluded === 1 ? " is" : "s are"} outside the tracked scope or in a model file that is switched off, and ${pv.excluded === 1 ? "is" : "are"} not included.`
            : "Everything under this row is included: nothing is outside the tracked scope or in a switched-off model file."}
        </div>
        <div style={{ marginTop: 4, color: "#8c8c8c" }}>
          You can undo it afterwards: the toast offers "Undo", and after it fades the
          <b> Undo last change</b> button on the Model elements panel does the same, until you make another change.
        </div>
      </div>
    ),
    // FULL SCREEN: without this the question renders on document.body, which
    // is not drawn while the stage is in the top layer - the owner sat on a
    // spinner waiting for a confirm he could not see (2026-09-30).
    getContainer: stageContainer,
    okText: "Set status", okButtonProps: pv.wouldDowngrade ? { danger: true } : undefined,
    onOk: () => resolve(true), onCancel: () => resolve(false),
  });
});

/**
 * Does this write have to ask first? EVERYTHING EXCEPT ONE NAMED ELEMENT.
 *
 * It used to be the other way round - "preview if it is a node, or more
 * than one id" - which listed the shapes known at the time and let every
 * other shape through unasked. On 2026-09-21 the select-all-filtered
 * selection arrived as { filters }: no node, no ids, so it went STRAIGHT
 * TO THE WRITE with no preview and no confirmation, on the one path built
 * so the owner could set a status "precisely". Found on staging when a
 * browser check pressed Set status and four elements changed without a
 * question being asked; never used on production (no bulk action there
 * since it shipped). The rule is now safe by default: a shape nobody
 * anticipated asks first.
 */
export function needsConfirmation(target) {
  if (!target || typeof target !== "object") return true;
  const ids = Array.isArray(target.uniqueIds) ? target.uniqueIds : [];
  const onlyOneElement = ids.length === 1
    && !target.nodeKeys && !target.recordNodes && !target.filters && !target.listId && !target.newSince
    // A custom category stands for every part inside every element it
    // matches - tens of thousands of them - so it asks first even when it
    // arrives beside a single clicked element. The rule above is "safe by
    // default"; this keeps it that way for the newest shape.
    && !target.derivedIds;
  return !onlyOneElement;
}

// Set a status on a tree node (or any selection) with the count preview
// first. Resolves to the server's answer, or null when the person said no
// or there was nothing to change.
export async function setStatusWithPreview(projectId, body, { what = "" } = {}) {
  const pv = await previewElementStatus(projectId, body);
  if (!pv.count) { message.info("Nothing in the tracked scope under that node"); return null; }
  if (pv.truncated) { message.warning(`More than ${n(pv.max)} elements - open the node and pick a smaller part`); return null; }
  // A SAVED EXCLUSION THE SERVER NO LONGER READS was skipped - said here,
  // before the yes, because it means the count includes what it named.
  if (Array.isArray(pv.skippedKeys) && pv.skippedKeys.length) {
    message.warning(`${pv.skippedKeys.length} saved exclusion${pv.skippedKeys.length === 1 ? " was" : "s were"} skipped and not subtracted: ${pv.skippedKeys[0].reason}`, 10);
  }
  if (!(await confirmBulk(pv, body.status, what))) return null;
  return setElementStatus(projectId, { ...body, confirmDowngrade: true });
}

// The success toast, with an Undo that reverses this person's last change.
export function announceStatus(projectId, r, status, onUndone) {
  const key = `status-${r.actionId || Date.now()}`;
  const undo = async () => {
    message.destroy(key);
    try {
      const u = await undoElementStatus(projectId);
      message.success(`Undone: ${n(u.undone)} element${u.undone === 1 ? "" : "s"} put back`);
      if (onUndone) onUndone(u);
    } catch (e) {
      message.error(e.message || "Could not undo");
    }
  };
  message.success({
    key, duration: 8,
    content: (
      <span>
        {n(r.count)} element{r.count === 1 ? "" : "s"} · {STATUS_LABEL[status]}
        {r.actionId ? <a style={{ marginLeft: 12 }} onClick={undo} role="button">Undo</a> : null}
      </span>
    ),
  });
}
